import { useEffect, useRef, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { Task, Reservation as TwilioReservation } from 'twilio-taskrouter'

import { useTwilioWorker } from '../aos/useTwilioWorker'
import { useCallStatus } from '../useCallStatus'
import { useMeetingContext } from '../useMeetingContext'

type Reservation = TwilioReservation & {
  task?: Task & {
    attributes: {
      facilityId: string
      deviceId: number
      requirement: 'checkIn' | 'other'
      guestConnectionStartTime: number
      language: 'en' | 'ja'
      property_translations: {
        en: {
          name: string
        }
        ja: {
          name: string
        }
      }
      device_translations: {
        en: {
          name: string
        }
        ja: {
          name: string
        }
      }
    }
  }
}

export function useReservation() {
  const { worker } = useTwilioWorker()
  const [reservation, setReservation] = useState<Reservation | null>(null)
  const executedWorker = useRef(false)
  const executedReservation = useRef(false)
  const { status, setStatus } = useCallStatus()
  const { setMeeting, setAttendee, setInformation } = useMeetingContext()
  const { t, i18n } = useTranslation('translation', {
    keyPrefix: 'page.meeting',
  })

  useEffect(() => {
    if (!reservation || !worker) {
      if (status === 'callback') {
        return
      }

      setStatus('waiting')
      return
    }

    if (
      import.meta.env.MODE.match(/development/) &&
      executedReservation.current
    ) {
      return
    }

    executedReservation.current = true

    setStatus('phoneRings')

    const translationKey = i18n.language === 'ja' ? 'ja' : 'en'

    const property =
      reservation.task.attributes.property_translations[translationKey].name

    const deviceName =
      reservation.task.attributes.device_translations?.[translationKey]?.name ??
      reservation.task.attributes.deviceId

    setInformation({
      property,
      deviceName,
      requirement: t(reservation.task.attributes.requirement),
      language: t(reservation.task.attributes.language),
    })

    reservation.on('accepted', (r) => {
      setMeeting(r.task.attributes.Meeting)
      setAttendee(r.task.attributes.Attendees[1])
      worker.activities.forEach((activity) => {
        if (activity.name === 'Unavailable') {
          activity.setAsCurrent()
        }
      })
    })

    // Assigned task to another worker
    reservation.on('rescinded', () => {
      setReservation(null)
      return
    })

    reservation.on('timeout', () => {
      if (status !== 'callback') {
        window.location.reload()
      }
      return
    })

    reservation.on('canceled', () => {
      setReservation(null)
      new Notification(t('missed'), {
        body: reservation.task.attributes.propertySlug,
        tag: `missedCall ${reservation.sid}`,
      })
      return
    })

    return () => {
      reservation.removeAllListeners()
      executedReservation.current = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reservation])

  useEffect(() => {
    if (!worker) {
      return
    }

    // Avoid calling createMeeting multiple times in development mode
    if (import.meta.env.MODE.match(/development/) && executedWorker.current) {
      return
    }

    if (!executedWorker.current && status === 'waiting') {
      executedWorker.current = true

      worker.on('ready', () => {
        worker.activities.forEach((activity) => {
          if (activity.name === 'Available' && !reservation) {
            activity.setAsCurrent()
          }
        })

        worker.reservations.forEach(async (r) => {
          // 本来は accept したら即 assigned -> complete になるはずが、
          // 何らかの理由で assigned で残っている場合は complete にする
          if (r.task.status === 'assigned') {
            await r.task.complete('')
          }

          // 画面が表示された段階で既にreservedがある場合
          if (r.task.status === 'reserved') {
            setReservation(r as Reservation)
          }
        })
      })

      worker.on('reservationCreated', (r: TwilioReservation) => {
        setReservation(r as Reservation)
      })
    }

    return () => {
      worker.removeAllListeners()
      executedWorker.current = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [worker])

  useEffect(() => {
    if (status === 'callback') {
      reservation?.removeAllListeners()
      worker?.removeAllListeners()
      setReservation(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status])

  async function accept() {
    async function handleAccept() {
      if (!reservation) {
        return
      }

      if (reservation.task.status === 'assigned') {
        alert('This call is already assigned to another worker')
        setReservation(null)
        return
      }

      await reservation.accept()
      setStatus('calling')
      reservation.task.complete('')
    }

    handleAccept()
  }

  return {
    reservation,
    accept,
  }
}
